<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ui } from '../editor/bridge';
import { buildSrcdoc, readBridgeMessage, vSandbox } from '../editor/sandbox';
import { fromSandbox, modulePanelHtml, modulePanels, onPanelPost, sendPanelMessage } from '../modules';
import { canvasDark } from '../theme';

/**
 * The panel of a module (API 2.0, manifest `contributes.panel`), shown in the right sidebar: the
 * module's sandboxed HTML. The module gets `{type:'opened'}` / `{type:'closed'}` and talks to it
 * through the bridge (`vp.post` / `vp.on` ⇄ `platform.postToPanel` / `on('panel.message')`).
 */
const props = defineProps<{ moduleId: string }>();

const panel = computed(() => modulePanels().find((p) => p.moduleId === props.moduleId) ?? null);
const html = ref<string | null>(null);
const frame = ref<HTMLIFrameElement | null>(null);
/** The panel takes the height of its content (up to the manifest height), reported by the bridge. */
const contentH = ref<number | null>(null);
const height = computed(() => Math.min(panel.value?.height ?? 420, Math.max(60, contentH.value ?? panel.value?.height ?? 420)));
let unsub: (() => void) | null = null;

function load(): void {
  const raw = modulePanelHtml(props.moduleId);
  html.value = raw ? buildSrcdoc(raw) : null;
}

function onMessage(e: MessageEvent): void {
  if (!frame.value || e.source !== frame.value.contentWindow) return;
  const b = readBridgeMessage(e.data);
  if (b) {
    if (b.size) contentH.value = Math.ceil(b.size.h);
    return;
  }
  const d = e.data as { vpView?: number; data?: unknown } | null;
  if (!d || d.vpView !== 1 || !fromSandbox(props.moduleId, d.data)) return;
  sendPanelMessage(props.moduleId, d.data);
}

onMounted(() => {
  window.addEventListener('message', onMessage);
  unsub = onPanelPost((m, data) => {
    if (m === props.moduleId) frame.value?.contentWindow?.postMessage({ vpView: 1, data }, '*');
  });
  load();
  sendPanelMessage(props.moduleId, { type: 'opened' });
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  unsub?.();
  sendPanelMessage(props.moduleId, { type: 'closed' });
});
watch(() => props.moduleId, load);
</script>

<template>
  <iframe
    v-if="html && panel"
    ref="frame"
    class="module-panel-frame"
    :data-panel="moduleId"
    :data-module="moduleId"
    v-sandbox="html"
    referrerpolicy="no-referrer"
    :title="panel.title"
    :style="{ height: height + 'px', pointerEvents: ui.readOnly ? 'none' : 'auto', colorScheme: canvasDark ? 'dark' : 'light' }"
  ></iframe>
</template>

<style scoped>
.module-panel-frame {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
}
</style>
